<template>
    <validation-observer ref="form" v-slot="{ meta, handleSubmit, errors, setFieldValue }" :initial-values="values">
        <b-form @submit.prevent="handleSubmit(_, save)">
            <busy-modal :show="loading" :overlay="true"></busy-modal>
            <b-row>
                <b-col md="6">
                    <validation-provider name="login_email" v-slot="{ field }" rules="required|email">
                        <form-input
                            v-bind="field"
                            floating-label="Login Email"
                            type="email"
                            :left-icon="['fas', 'envelope']"
                            :error="errors['login_email']"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider v-slot="{ field }" name="contact_email" rules="email">
                        <form-input
                            v-bind="field"
                            floating-label="Contact Email"
                            type="email"
                            :left-icon="['fas', 'envelope']"
                            :error="errors.contact_email"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row v-if="values.id">
                <b-col md="6">
                    <input type="text" style="display: none" />
                    <input type="password" style="display: none" />
                    <validation-provider v-slot="{ field }" name="password">
                        <form-input
                            v-bind="field"
                            autocomplete="new-password"
                            floating-label="Password"
                            type="password"
                            description="Fill in to change password, otherwise leave empty."
                            :left-icon="['fas', 'lock']"
                            :error="errors.password"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider v-slot="{ field }" name="reply_to_email" rules="required">
                        <form-select
                            v-bind="field"
                            floating-label="Guest Reply-to Email"
                            :left-icon="['fas', 'user']"
                            :options="replyToEmailOptions"
                            :error="errors.reply_to_email"
                        ></form-select>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider v-slot="{ field }" name="first_name" rules="required">
                        <form-input
                            v-bind="field"
                            floating-label="First Name"
                            :left-icon="['fas', 'user']"
                            :error="errors.first_name"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider v-slot="{ field }" name="last_name" rules="required">
                        <form-input
                            v-bind="field"
                            floating-label="Last Name"
                            :left-icon="['fas', 'user']"
                            :error="errors.last_name"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <form-checkbox @change="(val) => setFieldValue('is_active', val)" v-model="values.is_active">
                        Active
                    </form-checkbox>
                </b-col>
            </b-row>
            <hr />
            <span v-if='!userIsAdmin'>
                <h4>Calendar Tape Options</h4>
                <b-row>
                    <b-col md="6">
                        <form-checkbox
                            @change="(val) => setFieldValue('ctape_display_guest_phone', val)"
                            v-model="values.ctape_display_guest_phone"
                        >
                            Display Guest Phone
                        </form-checkbox>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <form-checkbox
                            @input="(val) => setFieldValue('ctape_display_guest_email', val)"
                            v-model="values.ctape_display_guest_email"
                        >
                            Display Guest Email
                        </form-checkbox>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <form-checkbox
                            @input="(val) => setFieldValue('ctape_display_within_admin_app', val)"
                            v-model="values.ctape_display_within_admin_app"
                        >
                            Display Within Lodgix Admin App
                        </form-checkbox>
                    </b-col>
                </b-row>
                <hr />
                <h4>Report Options</h4>
                <b-row>
                    <b-col md="6">
                        <form-checkbox
                            @input="(val) => setFieldValue('reports_display_cleaning_fees', val)"
                            v-model="values.reports_display_cleaning_fees"
                        >
                            Display Cleaning Fees on Reports
                        </form-checkbox>
                    </b-col>
                </b-row>
                <hr />
                <h4>Work Order Options</h4>
                <b-row>
                    <b-col md="6">
                        <form-checkbox
                            @input="(val) => setFieldValue('work_order_display_all_assignees', val)"
                            v-model="values.work_order_display_all_assignees"
                        >
                            Display All Assignees
                        </form-checkbox>
                    </b-col>
                </b-row>
            </span>
            <b-button class="mt-4 mb-4" type="submit" variant="primary" :disabled="loading || !meta.valid">
                Save
            </b-button>
        </b-form>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import isEmpty from 'lodash/isEmpty';
    import { formatResponseError } from '../../../utils/api';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';

    export default {
        name: 'TeamMemberDetailsForm',
        components: {
            BusyModal,
            FormCheckbox,
            FormInput,
            FormSelect,
        },
        props: {
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            ...mapGetters('header', ['userIsAdmin']),
        },
        data() {
            return {
                loading: false,
                values: this.getInitialValues(),
                replyToEmailOptions: [
                    {
                        value: 0,
                        text: 'Team Member Contact Email',
                    },
                    {
                        value: 1,
                        text: 'Subscriber Contact Email',
                    },
                ],
            };
        },
        mounted() {
            this.$refs.form.setValues(this.values);
        },
        methods: {
            getInitialValues() {
                let ctape_display_within_admin_app = this.initialValues.ctape_display_within_admin_app;
                if (ctape_display_within_admin_app === undefined) {
                    ctape_display_within_admin_app = true;
                }
                return {
                    id: this.initialValues.id || null,
                    first_name: this.initialValues.first_name || '',
                    last_name: this.initialValues.last_name || '',
                    is_active: this.initialValues.is_active || false,
                    login_email: this.initialValues.login_email || '',
                    contact_email: this.initialValues.contact_email || '',
                    password: null,
                    reply_to_email: this.initialValues.reply_to_email || 0,
                    ctape_display_guest_phone: this.initialValues.ctape_display_guest_phone || false,
                    ctape_display_guest_email: this.initialValues.ctape_display_guest_email || false,
                    reports_display_cleaning_fees: this.initialValues.reports_display_cleaning_fees || false,
                    work_order_display_all_assignees: this.initialValues.work_order_display_all_assignees || false,
                    ctape_display_within_admin_app: ctape_display_within_admin_app,
                };
            },
            async update(values) {
                await this.$store.dispatch('teamMember/updateTeamMember', values);
                this.successToast({ text: 'Team member was successfully updated.' });
            },
            async create(values) {
                const teamMember = await this.$store.dispatch('teamMember/createTeamMember', values);
                this.$emit('success-create', teamMember);
            },
            async save(values) {
                this.loading = true;

                try {
                    await (this.initialValues.id ? this.update(values) : this.create(values));
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>
